import * as React from "react";
import {Icon, Label} from "bloomer";
import Card from "../../../elements/Card";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import Button from "../../../elements/Button";
import {createTextExtractionJob, getTextExtractionJobResult} from "../../../api/models/TextExtraction";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import InformationCard from "../../../elements/InformationCard";
import ModelContentContainer from "../../../elements/ModelContentContainer";

interface ITextExtractionModelState {
  loading: boolean;
  file: any;
  fileErrorMessage: string;
  result: string;
  jobErrorMessage: string;
}

class TextExtractionModel extends React.Component<{}, ITextExtractionModelState> {
  public timeout: any = null;
  public fileInputRef: any = null;
  public fileInputStyle = {
    width: '100%'
  };

  public JOB_STATUS_CHECK_INTERVAL: number = 3000; // 3 seconds
  public JOB_STATUS_CHECK_MAX_COUNT: number = 200;
  public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png', 'application/pdf'];
  public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');

  constructor(props: {}) {
    super(props);

    this.state = {
      loading: false,
      file: null,
      fileErrorMessage: "",
      result: "",
      jobErrorMessage: ""
    };
  }

  public componentDidMount() {
    SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TEXT_EXTRACTION);
  }

  public componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  public handleFileChange = (e: any) => {
    this.setState({
      fileErrorMessage: ""
    });

    const file = e.target.files[0];
    if (file) {
      if (this.ALLOWED_FILE_TYPES.includes(file.type)) {
        this.setState({
          file
        });
      } else {
        this.setState({
          file: null,
          fileErrorMessage: "The model only accepts JPEG, PNG or PDF format files."
        });
      }
    }
  };

  public downloadResultAsFile = (e: any) => {
    e.target.blur();

    if (this.state.result) {
      const element = document.createElement("a");
      const file = new Blob([this.state.result], {type: 'text/plain'});
      element.href = URL.createObjectURL(file);
      element.download = "result.txt";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  }

  public cancelChecking = (errorMessage: string) => {
    clearTimeout(this.timeout);
    this.setState({
      loading: false,
      result: "",
      jobErrorMessage: errorMessage
    });
  };

  public checkJobStatus = async (jobId: string, count: number) => {
    if (count > this.JOB_STATUS_CHECK_MAX_COUNT) {
      this.cancelChecking("Timeout error.");
      return;
    }

    const response = await getTextExtractionJobResult(jobId);
    if (response) {
      if (response.status === 210) {
        this.timeout = setTimeout(() => {
          this.checkJobStatus(jobId, count + 1);
        }, this.JOB_STATUS_CHECK_INTERVAL);
      } else {
        let result = "";
        const responseData = response.data;
        if (responseData && responseData.result && responseData.result.text) {
          result = responseData.result.text;
        } else {
          this.setState({
            jobErrorMessage: "Unable to extract text."
          });
        }
        this.setState({
          loading: false,
          result
        });

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TEXT_EXTRACTION);
      }
    } else {
      this.cancelChecking("Error occurred.");
    }
  };

  public onSubmit = async (e: any) => {
    e.target.blur();

    if (this.state.loading) {
      return;
    }

    if (this.state.file && !this.state.fileErrorMessage) {
      this.setState({
        loading: true,
        result: "",
        jobErrorMessage: ""
      });

      const formData = new FormData();
      formData.append('file', this.state.file);

      let errorOccurred = false;

      const response = await createTextExtractionJob(formData).catch((error) => {
        if (ErrorUtils.requestLimitExceeded(error.response)) {
          this.setState({
            loading: false
          });
          toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
        } else {
          this.setState({
            loading: false,
            jobErrorMessage: "Couldn't start process."
          });
        }
        errorOccurred = true;
      });

      if (errorOccurred) {
        return;
      }

      if (response && response.data && response.data.job_id) {
        this.checkJobStatus(response.data.job_id, 1);
      } else {
        this.setState({
          loading: false,
          jobErrorMessage: "Couldn't start process."
        });
      }
    }
  };

  public onReset = (e: any) => {
    e.target.blur();

    this.setState({
      loading: false,
      file: "",
      fileErrorMessage: "",
      result: "",
      jobErrorMessage: ""
    });
    if (this.fileInputRef) {
      this.fileInputRef.value = "";
    }
    clearTimeout(this.timeout);
  };

  public render() {
    return <ModelContentContainer>
          <Card>
            <Label>Select Image or PDF File:</Label>
            <Spacer/>
            <input
                ref={(el) => this.fileInputRef = el}
                type="file"
                accept={this.ALLOWED_FILE_TYPES_STR}
                onChange={this.handleFileChange}
                style={this.fileInputStyle}
            />
            {
              this.state.fileErrorMessage && <div>
                  <InputWarning>{this.state.fileErrorMessage}</InputWarning>
              </div>
            }
            <Spacer/>
            <div>
              <Button onClick={this.onSubmit}>Submit</Button>
              <Button onClick={this.onReset} style={{marginLeft: "5px"}}>Reset</Button>
            </div>
            <Spacer/>
            {
              this.state.loading && <div>
                  <span>Loading...</span>
                  <Icon className="fas fa-spinner fa-spin"/>
              </div>
            }
            {
              !this.state.loading && this.state.result && <table style={{width: '100%'}}>
                  <tr>
                      <td style={{textAlign: 'left'}}>
                          <Label>Result:</Label>
                      </td>
                      <td style={{textAlign: 'right'}}>
                          <Button onClick={this.downloadResultAsFile} title="Download as .txt file">
                              <Icon className="far fa-file-alt" style={{marginRight: '5px'}}/>Download
                          </Button>
                      </td>
                  </tr>
                  <tr>
                      <td colSpan={2}>
                          <InformationCard>{this.state.result}</InformationCard>
                      </td>
                  </tr>
              </table>
            }
            {
              this.state.jobErrorMessage && <div>
                  <InputWarning>{this.state.jobErrorMessage}</InputWarning>
              </div>
            }
          </Card>
        </ModelContentContainer>;
  }
}

export default TextExtractionModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/TextExtraction/TextExtractionModel.tsx